/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import Media from 'react-media';
import { useSelector } from 'react-redux';
import { compose } from 'recompose';

// Helpers
import mbpLogger from 'mbp-logger';
import CUSTOM_BREAKPOINTS from '../../../style/customBreakpoints';
import { RecipientBookContextProvider } from './Common/Context/RecipientBookContext';
import { trackPage, trackData } from '../../../../../helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../../../helpers/tracking/hocs/withPageView';
import Loading from '../../../common/Loading';

// Components
import DesktopRecipientBook from './DesktopRecipientBook/DesktopRecipientBook';
import MobileRecipientBook from './MobileRecipientBook/MobileRecipientBook';
import { sortRecipients } from './Common/Helpers/Helpers';
import { getIsAuthenticatedStatus } from '../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import useRecipientBookListRes from './Common/Hooks/useRecipientBookListRes';

const RecipientBookContainer = () => {
    const breakpointExpr = `(max-width: ${(CUSTOM_BREAKPOINTS.tabLandscape)}px)`;
    const isAuthenticated = useSelector((state) => getIsAuthenticatedStatus(state));
    const userProfile = useSelector((state) => state.account.userProfile || state.member.profile);
    const contactId = userProfile?.ContactId || '';

    const {
        isLoading,
        data,
        status,
        error,
        refetch, // <- manually triggers "refetch"
    } = useRecipientBookListRes(contactId);

    useEffect(() => {
        if (isAuthenticated && contactId) {
            refetch(contactId);
        }
    }, [isAuthenticated, contactId, refetch])

    trackData({
        seoData: {page_title: "Recipient book | 1800Flowers"},
        action: 'track_seo_data',
    });

    if (isLoading) {
        return <Loading />;
    }

    if (status === 'error') {
        let recipientBookListResError = 'GENERIC_API_ERROR';
        if (error.message === 'Request failed with status code 404') {
            recipientBookListResError = 'NO_RECIPIENTS_FOUND';
        }
        mbpLogger.logError({
            function: 'getRecipientBookList',
            appName: process.env.npm_package_name,
            module: 'RecipientBookContainer',
            message: `Error While calling recipient book for contactId ${contactId}`,
            error,
        });

        return (
            <>
                <Media query={breakpointExpr}>
                    {(matches) => (
                        matches ? (
                            <RecipientBookContextProvider>
                                <MobileRecipientBook
                                    recipients={{}}
                                    recipientBookListResError={recipientBookListResError}
                                />
                            </RecipientBookContextProvider>)
                            : (
                            <RecipientBookContextProvider>
                                <DesktopRecipientBook
                                    recipients={{}}
                                    recipientBookListResError={recipientBookListResError}
                                />
                            </RecipientBookContextProvider>)
                    )}
                </Media>
            </>
        );
    }

    if (status === 'success') {
        const {
            data: recipientBookListRes,
            data: {
                addressBookResponse: {
                    contactId,
                    reminderEmail,
                    totalEntries
                }
            }
        } = data;

        const sortedRecipientBookListRes = {
            addressBookResponse: {
                addressList: sortRecipients('asc', recipientBookListRes.addressBookResponse),
                totalEntries,
                contactId,
                reminderEmail,
                sortOption: 'asc'
            },
        };
        return (
            <>
                <Media query={breakpointExpr}>
                    {(matches) => (
                        matches ? (
                            <RecipientBookContextProvider>
                                <MobileRecipientBook
                                    recipients={sortedRecipientBookListRes}
                                    recipientBookListResError={''}
                                />
                            </RecipientBookContextProvider>)
                            : (
                            <RecipientBookContextProvider>
                                <DesktopRecipientBook
                                    recipients={sortedRecipientBookListRes}
                                    recipientBookListResError={''}
                                />
                            </RecipientBookContextProvider>)
                    )}
                </Media>
            </>
        );
    }

    return null;
};

const enhance = compose(
    trackPage({
        type: 'recipientbook',
        name: 'recipientbook page',
        accountPage: true,
    }),
    withPageView({
        pageViewSuccessTest: () => true,
    }),
);

export default enhance(RecipientBookContainer);
